import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Building2, Coins, MessageSquare, Target, Users, TrendingUp, Crown, Medal, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';

const API = `${window.location.origin}/api`;

const CATEGORIES = [
  { id: 'businesses', label: 'Businesses', icon: Building2, color: 'amber' },
  { id: 'mel_earned', label: 'MEL Earned', icon: Coins, color: 'purple' },
  { id: 'reviews', label: 'Reviews', icon: MessageSquare, color: 'emerald' },
  { id: 'bounties', label: 'Bounties', icon: Target, color: 'rose' },
];

const PERIODS = [
  { id: 'all_time', label: 'All Time' },
  { id: 'monthly', label: 'This Month' },
  { id: 'weekly', label: 'This Week' },
];

const RANK_STYLES = {
  1: { icon: Crown, bg: 'from-amber-500/20 to-amber-600/5', border: 'border-amber-500/40', text: 'text-amber-400', badge: 'bg-amber-500' },
  2: { icon: Medal, bg: 'from-zinc-300/15 to-zinc-400/5', border: 'border-zinc-400/30', text: 'text-zinc-300', badge: 'bg-zinc-400' },
  3: { icon: Award, bg: 'from-orange-500/15 to-orange-600/5', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500' },
};

function formatValue(value, category) {
  if (category === 'mel_earned') return `${Number(value).toLocaleString()} MEL`;
  return Number(value).toLocaleString();
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card rounded-xl p-4 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className={`w-5 h-5 mx-auto mb-2 text-${color}-400`} />
      <div className="text-2xl font-bold text-white">{Number(value).toLocaleString()}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function LeaderboardEntry({ entry, category, index }) {
  const isTop3 = entry.rank <= 3;
  const style = RANK_STYLES[entry.rank] || {};
  const RankIcon = style.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
        isTop3
          ? `bg-gradient-to-r ${style.bg} ${style.border}`
          : 'bg-zinc-900/40 border-zinc-800/50 hover:border-zinc-700'
      }`}
      data-testid={`leaderboard-entry-${entry.rank}`}
    >
      {/* Rank */}
      <div className="w-10 flex-shrink-0 text-center">
        {isTop3 ? (
          <div className={`w-8 h-8 mx-auto rounded-full ${style.badge} flex items-center justify-center`}>
            <RankIcon className="w-4 h-4 text-black" />
          </div>
        ) : (
          <span className="text-zinc-500 font-mono text-sm">#{entry.rank}</span>
        )}
      </div>

      {/* Avatar */}
      <div className="w-10 h-10 flex-shrink-0 rounded-full bg-zinc-800 overflow-hidden">
        {entry.picture ? (
          <img src={entry.picture} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-sm font-bold">
            {entry.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate ${isTop3 ? style.text : 'text-white'}`}>
          {entry.name}
        </div>
        {entry.member_since && (
          <div className="text-xs text-zinc-600">
            Member since {new Date(entry.member_since).getFullYear()}
          </div>
        )}
      </div>

      {/* Value */}
      <div className="text-right flex-shrink-0">
        <div className={`text-lg font-bold ${isTop3 ? style.text : 'text-white'}`}>
          {formatValue(entry.value, category)}
        </div>
        <div className="text-xs text-zinc-600">{entry.metric}</div>
      </div>
    </motion.div>
  );
}

export default function LeaderboardPage() {
  const [category, setCategory] = useState('businesses');
  const [period, setPeriod] = useState('all_time');
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/leaderboard/stats`);
        if (res.ok) setStats(await res.json());
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/leaderboard?category=${category}&period=${period}&limit=20`);
        if (res.ok) {
          const data = await res.json();
          setEntries(data.entries || []);
        }
      } catch (e) {
        console.error('Failed to fetch leaderboard:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [category, period]);

  const activeColor = CATEGORIES.find(c => c.id === category)?.color || 'purple';

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-24 pb-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <Trophy className="w-8 h-8 text-amber-400" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
              Leaderboard
            </h1>
          </div>
          <p className="text-zinc-500 text-base max-w-md mx-auto">
            Top contributors powering the Blackified community
          </p>
        </motion.div>

        {/* Community Stats */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10"
            data-testid="community-stats"
          >
            <StatCard icon={Users} label="Members" value={stats.total_users} color="purple" />
            <StatCard icon={Building2} label="Businesses" value={stats.total_businesses} color="amber" />
            <StatCard icon={MessageSquare} label="Reviews" value={stats.total_reviews} color="emerald" />
            <StatCard icon={Coins} label="MEL Earned" value={stats.total_mel_earned} color="rose" />
          </motion.div>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="category-tabs">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = category === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? `bg-${cat.color}-500/20 text-${cat.color}-400 border border-${cat.color}-500/40`
                    : 'bg-zinc-900/60 text-zinc-500 border border-zinc-800 hover:border-zinc-700 hover:text-zinc-300'
                }`}
                data-testid={`tab-${cat.id}`}
              >
                <Icon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Period Filter */}
        <div className="flex gap-2 mb-8" data-testid="period-filter">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                period === p.id
                  ? 'bg-white/10 text-white'
                  : 'text-zinc-600 hover:text-zinc-400'
              }`}
              data-testid={`period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Leaderboard List */}
        <div className="space-y-2" data-testid="leaderboard-list">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="h-[72px] rounded-xl bg-zinc-900/40 animate-pulse" />
            ))
          ) : entries.length > 0 ? (
            entries.map((entry, index) => (
              <LeaderboardEntry
                key={entry.user_id}
                entry={entry}
                category={category}
                index={index}
              />
            ))
          ) : (
            <div className="text-center py-16">
              <Trophy className="w-12 h-12 text-zinc-800 mx-auto mb-4" />
              <p className="text-zinc-600 text-lg mb-1">No rankings yet</p>
              <p className="text-zinc-700 text-sm">
                Be the first to contribute!{' '}
                <Link to="/submit-business" className="text-amber-500 hover:underline">
                  Submit a business
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
